import { Button, Divider, Paper, Typography, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, TextField, Box, Tab, Tabs, Switch } from '@material-ui/core';
import { AnyMap } from '@reduxjs/toolkit/node_modules/immer/dist/internal';
import React, { useEffect } from 'react';
import Chart from "react-google-charts";
import { GoogleDataTable } from 'react-google-charts/dist/types';
import Carousel from 'react-material-ui-carousel';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../app/hooks';

import { getFanDetails, changePerformanceCurvePoint, changePerformanceCurveAirflow, changePerformanceCurveAngle, getFanData, getPressureCurveMode, setPressureCurveMode } from '../../app/reducer/project.reducer';

type TableData = (number | string | null)[][];

function cleanChartData(data: TableData): TableData {
  return data.map((row, rowIndex) => {
    if (rowIndex === 0) return row; // header

    return row.map((val, colIndex) => {
      if (colIndex === 0) return val; // airflow stays

      if (val == null) return null;

      const num = Number(val);
      return isNaN(num) ? null : num;
    });
  });
}

export function appendInterpolatedPoints(data: TableData): TableData {

  const headers = data[0];
  const airflowIndex = 0;
  const pvIndex = headers.indexOf("PV");

  const angleCols: number[] = [];

  headers.forEach((h, i) => {
    const v = Number(h);
    if (!isNaN(v)) angleCols.push(i);
  });

  const rows = data.slice(1);
  const newRows: (number | null)[][] = [];

  for (const col of angleCols) {
    for (let i = 0; i < rows.length - 1; i++) {

      const r1 = rows[i];
      const r2 = rows[i + 1];

      const x1 = Number(r1[airflowIndex]);
      const x2 = Number(r2[airflowIndex]);

      const y1 = r1[col];
      const y2 = r2[col];

      if (y1 == null || y2 == null) continue;

      const y1n = Number(y1);
      const y2n = Number(y2);

      const pv1 = r1[pvIndex];
      const pv2 = r2[pvIndex];

      // PV intersection
      if (pv1 != null && pv2 != null) {

        const d1 = y1n - Number(pv1);
        const d2 = y2n - Number(pv2);

        if (d1 * d2 <= 0) {

          const t = Math.abs(d1) / (Math.abs(d1) + Math.abs(d2));

          const airflow = x1 + (x2 - x1) * t;
          const pressure = y1n + (y2n - y1n) * t;

          const row = new Array(headers.length).fill(null);

          row[airflowIndex] = airflow;
          row[col] = pressure;
          row[pvIndex] = pressure;

          newRows.push(row);
        }
      }

      // Zero-pressure intersection
      if (y1n >= 0 && y2n < 0) {

        const t = y1n / (y1n - y2n);
        const airflow = x1 + (x2 - x1) * t;

        const row = new Array(headers.length).fill(null);

        row[airflowIndex] = airflow;
        row[col] = 0;

        newRows.push(row);
      }
    }
  }

  const result = [...rows, ...newRows];

  // ✅ Proper sorting
  const sortedRows = result.sort(
    (a, b) => Number(a[airflowIndex]) - Number(b[airflowIndex])
  );

  return [headers, ...sortedRows];
}

export default function PerformanceCurveStatic({ ...props }: any) {
  const pressureCurveChoosen = useAppSelector(getPressureCurveMode);
  const curveType = pressureCurveChoosen === "static";


  const [pressureReady, setPressureReady] = React.useState<boolean>(false);
  const [efficiencyReady, setEfficiencyReady] = React.useState<boolean>(false);
  const [angles, setAngles] = React.useState<any[]>([]);
  const [graphPressureData, setGraphPressureData] = React.useState<any[]>([]);
  const [graphShaftData, setGraphShaftData] = React.useState<any[]>([]);
  const [graphEfficiencyData, setGraphEfficiencyData] = React.useState<any[]>([]);
  const [index, setIndex] = React.useState(0);

  // const [curveType, setCurveType] = React.useState<boolean>(pressureCurveChoosen=='total'?false:true);
  const [designPoint, setDesignPoint] = React.useState<boolean>(true);

  const dispatch = useDispatch();
  const fanDetails = useAppSelector(getFanDetails);
  console.log(fanDetails)
  const fanData = useAppSelector(getFanData);
  var options = {
    curveType: 'function',
    legend: { position: 'right' },
    
    crosshair: { trigger: 'selection' },
    hAxis: {
      title: 'Airflow (m³/sec)', // X axis unit
      textStyle: {
        fontSize: 22, // 🔹 x-axis labels
      },
      titleTextStyle: {
        fontSize: 18, // 🔹 x-axis title
        bold: true,
      },
    },
    vAxis: {
      title: curveType ? 'Static Pressure(Pascal)' : 'Total Pressure(Pascal)', // <-- Add unit here
      textStyle: {
        fontSize: 22, // 🔹 x-axis labels
      },
      titleTextStyle: {
        fontSize: 18, // 🔹 x-axis title
        bold: true,
      },
    }
  };
  var options2 = {
    curveType: 'function',
    legend: { position: 'right' },
    crosshair: { trigger: 'selection' },
    hAxis: {
      title: 'Airflow (m³/sec)', // X axis unit
      textStyle: {
        fontSize: 22, // 🔹 x-axis labels
      },
      titleTextStyle: {
        fontSize: 18, // 🔹 x-axis title
        bold: true,
      },
    },
    vAxis: {
      title: 'Fan Shaft Power (kW)', // <-- Add unit here
      textStyle: {
        fontSize: 22, // 🔹 x-axis labels
      },
      titleTextStyle: {
        fontSize: 18, // 🔹 x-axis title
        bold: true,
      },
    }
  };
  var options3 = {
    curveType: 'function',
    legend: { position: 'right' },
    crosshair: { trigger: 'selection' },
    hAxis: {
      title: 'Airflow (m³/sec)', // X axis unit
      textStyle: {
        fontSize: 22, // 🔹 x-axis labels
      },
      titleTextStyle: {
        fontSize: 18, // 🔹 x-axis title
        bold: true,
      },
    },
    vAxis: {
      title: curveType ? "Static Efficiency (%)" : "Total Efficiency (%)", // <-- Add unit here
      textStyle: {
        fontSize: 22, // 🔹 x-axis labels
      },
      titleTextStyle: {
        fontSize: 18, // 🔹 x-axis title
        bold: true,
      },
    }
  };

  useEffect(() => {

    populatePressureGraphDataFromProps()
    populateShaftGraphDataFromProps()
    populateEfficiencyGraphDataFromProps()

  }, [props.data, curveType, designPoint]);

  const onPlotpointChage = (flow: string, angle: string, pressure: number) => {

    let type = "total";
    if (curveType) {
      type = "static";
    }

    // alert(flow+" "+angle+" "+pressure);
    dispatch(changePerformanceCurvePoint({ flow, angle, pressure, type }))

  }
  const onPlotpointChageAirflow = (flow: number, index: number) => {

    let type = "total";
    if (curveType) {
      type = "static";
    }
    dispatch(changePerformanceCurveAirflow({ flow, index, type }))

  }

  const onPlotpointChageAngle = (newAngle: string, index: number, oldAngle: string) => {

    let type = "total";
    if (curveType) {
      type = "static";
    }
    dispatch(changePerformanceCurveAngle({ newAngle, index, oldAngle }))

  }




  const populatePressureGraphDataFromProps = async () => {
    const { data } = props;
    console.log(data);
    let tempGraphPressureData = [];
    // const { netFreeArea } = fanData;
    const { air_density, req_airflow, pressure } = fanDetails;
    const d1 = fanDetails.fan_diameter ? parseFloat(fanDetails.fan_diameter) : fanData.fanDiameter;
    const d2 = fanDetails.fan_ring_diameter ? fanDetails.fan_ring_diameter : (d1 + (d1 * 2 * 0.0045)).toFixed(3);
    const netFreeArea = 3.14 * (Math.pow(d2, 2) - Math.pow(fanData.hub.hubDiameter, 2)) / 4;
    console.log(fanDetails, fanData, netFreeArea);

    let angles: string[] = [];
    let graphHeaderData = ['Airflow'];
    let maxP = 0;

    // Build angles array and headers
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < data[i].plotPoints.length; j++) {
        for (let k = 0; k <= angles.length; k++) {
          if (angles.length === 0) {
            angles.push(data[i].plotPoints[j].graphAngle);
            graphHeaderData.push(data[i].plotPoints[j].graphAngle);
          }
          if (data[i].plotPoints[j].graphAngle === angles[k]) {
            break;
          } else if (k === angles.length - 1) {
            angles.push(data[i].plotPoints[j].graphAngle);
            graphHeaderData.push(data[i].plotPoints[j].graphAngle);
          }
        }
      }
    }
    graphHeaderData.push("PV");
    graphHeaderData.push("System Res Cu (t)");
    console.log(angles, graphHeaderData)

    setAngles(angles);
    tempGraphPressureData.push(graphHeaderData);

    const rpm1 = parseInt(props.rpm);
    const rpm2 = fanDetails.fan_speed ? parseInt(fanDetails.fan_speed) : rpm1;
    const air_density1 = fanData.airDensity ? fanData.airDensity : 1.2;
    const air_density2 = fanDetails.air_density ? fanDetails.air_density : air_density1;
    var pvValue = 0;
    for (let i = 0; i < data.length; i++) {
      let GraphPressureDataRow = [];
      const pData = data[i].flow * (rpm2 / rpm1);
      console.log("ScalingF", (rpm2 / rpm1), data[i].flow);
      GraphPressureDataRow.push(pData);

      // Calculate PV value for this airflow point first
      pvValue = Math.pow((pData / netFreeArea), 2) * 0.5 * (parseFloat(air_density2));
      console.log(pData, netFreeArea, air_density2, pvValue);
      console.log(netFreeArea, air_density)


      for (let j = 1; j < graphHeaderData.length; j++) {
        for (let k = 0; k < data[i].plotPoints.length; k++) {
          if (graphHeaderData[j] === data[i].plotPoints[k].graphAngle) {
            let tp;
            let calculatedPressure;
            const rpm1 = parseInt(props.rpm);
            const rpm2 = fanDetails.fan_speed ? parseInt(fanDetails.fan_speed) : rpm1;
            const air_density1 = fanData.airDensity ? fanData.airDensity : 1.2;
            const air_density2 = fanDetails.air_density ? fanDetails.air_density : air_density1;
            tp = parseFloat(data[i].plotPoints[k].total_pressure);
            if (curveType) {
              calculatedPressure = tp * (rpm2 / rpm1) * (rpm2 / rpm1) * (air_density2 / air_density1) - pvValue;
            } else {
              calculatedPressure = tp * (rpm2 / rpm1) * (rpm2 / rpm1) * (air_density2 / air_density1);
            }


            const finalPressure = Math.max(calculatedPressure, pvValue); // to make sure that total pressure curve does not cut the PV line
            if (curveType) //for static
              GraphPressureDataRow.push(calculatedPressure);
            else
              GraphPressureDataRow.push(finalPressure);
            maxP = calculatedPressure > maxP ? calculatedPressure : maxP;
            break;
          }

          if (graphHeaderData[j] === "PV") {
            GraphPressureDataRow.push(pvValue);
            break;
          }

          if (graphHeaderData[j] === "System Res Cu (t)") {
            const percentage = maxP * 0.3;
            if (curveType) { // if the cureve is static
              let g9: any = Math.pow(((data[i].flow) * ((rpm2 / rpm1)) / parseFloat(req_airflow)), 2) * (fanDetails?.pressure_type == 'static' ? parseFloat(pressure) : parseFloat(pressure) - (0.5 * air_density2 * Math.pow((req_airflow / netFreeArea), 2)));
              GraphPressureDataRow.push(g9 > (maxP + percentage) ? null : g9);  // this will let the angle curve cut the PV lines. 
            } else {
              const rpm1 = parseInt(props.rpm);
              const rpm2 = fanDetails.fan_speed ? parseInt(fanDetails.fan_speed) : rpm1;
              const g7 = data[i].flow * (rpm2 / rpm1);
              const g9 = Math.pow((g7 / parseFloat(req_airflow)), 2) * (fanDetails?.pressure_type == 'total' ? parseFloat(pressure) : parseFloat(pressure) + (0.5 * air_density2 * Math.pow((req_airflow / netFreeArea), 2)));
              GraphPressureDataRow.push(g9 > (maxP + percentage) ? null : g9);
            }
            break;
          }

          // Design points
          if (k === (data[i].plotPoints.length - 1)) {
            GraphPressureDataRow.push(null);
          }
        }
      }

      tempGraphPressureData.push(GraphPressureDataRow);
    }

    // Design point handling (unchanged)
    if (designPoint) {
      let tempGraphPressureDataDesignPoint = tempGraphPressureData;
      tempGraphPressureDataDesignPoint[0].push("Design Point");
      console.log(tempGraphPressureDataDesignPoint);
      for (let i = 1; i < tempGraphPressureData.length; i++) {
        tempGraphPressureDataDesignPoint[i].push(null);
      }

      let GraphPressureDataRow = [];
      GraphPressureDataRow.push(parseInt(fanDetails.req_airflow));

      for (let j = 2; j < graphHeaderData.length; j++) {
        GraphPressureDataRow.push(null);
      }

      if (curveType) { // curveType is true for static presssure 
        if (fanDetails.pressure_type === "static") { console.log("1"); GraphPressureDataRow.push(parseFloat(fanDetails.pressure)); } // Static pressure at design point
        else { console.log("2"); GraphPressureDataRow.push(parseFloat(fanDetails.pressure) - (0.5 * air_density2 * Math.pow((req_airflow / netFreeArea), 2))); }
      } else {
        if (fanDetails.pressure_type === "static") { console.log("3", fanDetails.pressure, 0.5 * air_density2 * Math.pow((req_airflow / netFreeArea), 2)); GraphPressureDataRow.push(parseFloat(fanDetails.pressure) + (0.5 * air_density2 * Math.pow((req_airflow / netFreeArea), 2))); } // Static pressure at design point
        else { console.log("4"); GraphPressureDataRow.push(parseInt(fanDetails.pressure)); } // Total pressure at design point
      }

      // GraphPressureDataRow.push(parseInt(fanDetails.pressure));
      tempGraphPressureData.push(GraphPressureDataRow);
    }
    console.log(tempGraphPressureData)
    const res = appendInterpolatedPoints(tempGraphPressureData)
    console.log(res);
    setGraphPressureData(tempGraphPressureData);
    console.log(tempGraphPressureData);
    setPressureReady(true);
  }

  const populateShaftGraphDataFromProps = async () => {

    const { data } = props;
    let tempGraphShaftData = [];

    let angles: string[] = [];
    let graphHeaderData = ['Airflow'];

    for (let i = 0; i < data.length; i++) {

      for (let j = 0; j < data[i].plotPoints.length; j++) {

        for (let k = 0; k <= angles.length; k++) {

          if (angles.length === 0) {
            angles.push(data[i].plotPoints[j].graphAngle);
            graphHeaderData.push(data[i].plotPoints[j].graphAngle);
          }

          if (data[i].plotPoints[j].graphAngle === angles[k]) {
            break;
          } else if (k === angles.length - 1) {
            angles.push(data[i].plotPoints[j].graphAngle);
            graphHeaderData.push(data[i].plotPoints[j].graphAngle);
          }
        }

      }

    }


    setAngles(angles);
    tempGraphShaftData.push(graphHeaderData);

    const rpm1 = parseInt(props.rpm);
    const rpm2 = fanDetails.fan_speed ? parseInt(fanDetails.fan_speed) : rpm1;
    const air_density1 = fanData.airDensity ? fanData.airDensity : 1.2;
    const air_density2 = fanDetails.air_density ? fanDetails.air_density : air_density1;

    for (let i = 0; i < data.length; i++) {
      let GraphShaftDataRow: any = [];
      const pData = data[i].flow * (rpm2 / rpm1);
      GraphShaftDataRow.push(pData);

      for (let j = 1; j < graphHeaderData.length; j++) {

        for (let k = 0; k < data[i].plotPoints.length; k++) {

          if (graphHeaderData[j] === data[i].plotPoints[k].graphAngle) {

            const tp1 = parseFloat(data[i].plotPoints[k].total_pressure);
            // const sp1 = parseFloat(data[i].plotPoints[k].static_pressure);
            let pData = tp1 * (rpm2 / rpm1) * (rpm2 / rpm1) * (air_density2 / air_density1);
            const aData = data[i].flow * (rpm2 / rpm1);
            GraphShaftDataRow.push((pData * aData * 0.1) / (data[i].plotPoints[k].efficiency));
            console.log(pData, aData, data[i].plotPoints[k].efficiency)
            break;
          }


          if (k === (data[i].plotPoints.length - 1)) {
            GraphShaftDataRow.push(null);
          }

        }

      }

      tempGraphShaftData.push(GraphShaftDataRow);
    }

    if (designPoint) {

      let tempGraphPressureDataDesignPoint = tempGraphShaftData;

      tempGraphPressureDataDesignPoint[0].push("Design Point");

      for (let i = 1; i < tempGraphShaftData.length; i++) {
        tempGraphPressureDataDesignPoint[i].push(null);
      }

      let GraphPressureDataRow = [];
      GraphPressureDataRow.push(parseInt(fanDetails.req_airflow));

      for (let j = 2; j < graphHeaderData.length; j++) {
        GraphPressureDataRow.push(null);
      }


      // let d35: any = null;
      // let d38: any = null;
      // const d1 = fanDetails.fan_diameter ? fanDetails.fan_diameter : fanData.fanDiameter;
      // const d2 = fanDetails.fan_ring_diameter ? fanDetails.fan_ring_diameter : d1 * 1.05;
      // const d17 = fanDetails.air_density;
      // const d34 = fanDetails.req_airflow
      // const d45 = fanData.designPoint?.expectedEfficiency;
      // const d31 = 3.14 * (Math.pow(d2, 2) - Math.pow(fanData.hub.hubDiameter, 2)) / 4;

      // const d46 = d34 / d31;
      // const d36: any = 0.5 * d17 * Math.pow(d46, 2);
      // const d37 = d36 - (0.5 * d17 * (Math.pow(d34 / (3.14 * (fanDetails.discharge_dia ?? 1) * (fanDetails.discharge_dia ?? 1) / 4), 2)) * (fanDetails.diffuser_eff ?? 1) / 100);

      // d1: Fan diameter (primary diameter of the fan)
      const d1 = fanDetails.fan_diameter ? parseFloat(fanDetails.fan_diameter) : fanData.fanDiameter;

      // d2: Fan ring diameter (diameter including the ring around the fan)
      const d2 = fanDetails.fan_ring_diameter ? fanDetails.fan_ring_diameter : (d1 + (d1 * 2 * 0.0045)).toFixed(3);

      // d17: Air density (density of air at operating conditions)
      const d17 = fanDetails.air_density;

      // d34: Required airflow (design airflow for the fan)
      const d34 = fanDetails.req_airflow;

      // d45: Expected efficiency at design point
      const d45 = fanData.designPoint?.expectedEfficiency;

      // d31: Net free area for airflow (area available for air to pass through, excluding hub)
      const d31 = 3.14 * (Math.pow(d2, 2) - Math.pow(fanData.hub.hubDiameter, 2)) / 4;
      console.log(d31)
      // d46: Air velocity through the fan
      const d46 = d34 / d31;

      // d36: Velocity pressure at design point
      const d36: any = 0.5 * d17 * Math.pow(d46, 2);

      // d37: Diffuser pressure correction
      const d37 = d36 - (0.5 * d17 * (Math.pow(d34 / (3.14 * (fanDetails.discharge_dia ?? 1) * (fanDetails.discharge_dia ?? 1) / 4), 2)) * (fanDetails.diffuser_eff ?? 1) / 100);

      // d35: Static pressure at design point (after corrections)
      // d38: Total pressure at design point (after corrections)
      let d35: any = null;
      let d38: any = null;
      //find out what is total static pressure and total pressure here
      //
      /* if(curveType){ //CHECK
         d35 = fanDetails.pressure;
         d38 = (parseFloat(fanDetails.pressure) + parseFloat(d36) - (isNaN(d37)?0:d37));
       }else{
         d38 = fanDetails.pressure;
         d35 = (fanDetails.pressure - (isNaN(d37)?0:d37));
       }*/

      // d38 = fanDetails.pressure;
      // d35 = (fanDetails.pressure - (isNaN(d37)?0:d37));
      debugger;
      // Find out what is total static pressure and total pressure here

      if (fanDetails.pressure_type === "static") { //CHECK
        d38 = (parseFloat(fanDetails.pressure) + parseFloat(d36) - (isNaN(d37) ? 0 : d37));
        d35 = fanDetails.pressure;

      } else {
        d38 = fanDetails.pressure;
        d35 = (parseFloat(fanDetails.pressure) - parseFloat(d36) + (isNaN(d37) ? 0 : d37));
      }
      // d39: Shaft power at design point
      const d39 = d34 * d38 * 0.1 / d45;
      console.log(d36,d34,d38,d45,d37,d39,d17)  
      GraphPressureDataRow.push(parseInt(d39.toString()));
      tempGraphShaftData.push(GraphPressureDataRow);

    }
    setGraphShaftData(tempGraphShaftData);
    console.log(tempGraphShaftData);
  }

  const populateEfficiencyGraphDataFromProps = async () => {

    const { data } = props;
    // const { netFreeArea } = fanData;
    const { air_density, req_airflow, static_pressure } = fanDetails;

    const d1 = fanDetails.fan_diameter ? parseFloat(fanDetails.fan_diameter) : fanData.fanDiameter;
    const d2 = fanDetails.fan_ring_diameter ? fanDetails.fan_ring_diameter : (d1 + (d1 * 2 * 0.0045)).toFixed(3);
    const netFreeArea = 3.14 * (Math.pow(d2, 2) - Math.pow(fanData.hub.hubDiameter, 2)) / 4;
    let tempGraphEfficiencyData: any = [];

    let angles: string[] = [];
    let graphHeaderData = ['Airflow'];

    for (let i = 0; i < data.length; i++) {

      for (let j = 0; j < data[i].plotPoints.length; j++) {

        for (let k = 0; k <= angles.length; k++) {

          if (angles.length === 0) {
            angles.push(data[i].plotPoints[j].graphAngle);
            graphHeaderData.push(data[i].plotPoints[j].graphAngle);
          }

          if (data[i].plotPoints[j].graphAngle === angles[k]) {
            break;
          } else if (k === angles.length - 1) {
            angles.push(data[i].plotPoints[j].graphAngle);
            graphHeaderData.push(data[i].plotPoints[j].graphAngle);
          }
        }

      }

    }

    // graphHeaderData.push("PV");
    // graphHeaderData.push("System Res Cu (t)");

    setAngles(angles);
    tempGraphEfficiencyData.push(graphHeaderData);



    const rpm1 = parseInt(props.rpm);
    const rpm2 = fanDetails.fan_speed ? parseInt(fanDetails.fan_speed) : rpm1;
    console.log(rpm1, rpm2);
    const air_density1 = fanData.airDensity ? fanData.airDensity : 1.2;
    const air_density2 = fanDetails.air_density ? fanDetails.air_density : air_density1;

    for (let i = 0; i < data.length; i++) {

      let GraphEfficiencyDataRow = [];
      const pData = data[i].flow * (rpm2 / rpm1);
      GraphEfficiencyDataRow.push(pData);


      for (let j = 1; j < graphHeaderData.length; j++) {

        for (let k = 0; k < data[i].plotPoints.length; k++) {

          if (graphHeaderData[j] === "PV") {
            GraphEfficiencyDataRow.push(null);
            break;
          }

          if (graphHeaderData[j] === "System Res Cu (t)") {
            GraphEfficiencyDataRow.push(null);
            break;
          }

          if (graphHeaderData[j] === data[i].plotPoints[k].graphAngle) {
            if (curveType) {
              //const g2 = data[i].flow;
              const rpm1 = parseInt(props.rpm);
              const rpm2 = fanDetails.fan_speed ? parseInt(fanDetails.fan_speed) : rpm1;
              const air_density1 = fanData.airDensity ? fanData.airDensity : 1.2;
              const air_density2 = fanDetails.air_density ? fanDetails.air_density : air_density1;
              const g2 = data[i].flow * (rpm2 / rpm1);

              const g5 = ((g2 / netFreeArea) ** 2) * 0.5 * (parseFloat(air_density2)); //this is pressure velocity
              const g7 = ((data[i].plotPoints[k].total_pressure * (rpm2 / rpm1) * (rpm2 / rpm1) * (air_density2 / air_density1)) - g5); //static pressure
              const g6 = (data[i].plotPoints[k].total_pressure * (rpm2 / rpm1) * (rpm2 / rpm1) * (air_density2 / air_density1) * g2) / (10 * data[i].plotPoints[k].efficiency);
              // console.log(g2,g5,g7,g6);
              // console.log(data[i].plotPoints[k].total_pressure*(rpm2 / rpm1)*(rpm2/rpm1) * (air_density2 / air_density1) ,data[i].flow,data[i].plotPoints[k].efficiency)
              GraphEfficiencyDataRow.push((g2 * g7 * 0.1) / g6);//g6 is fan shaft power
            } else {
              GraphEfficiencyDataRow.push(parseFloat(data[i].plotPoints[k].efficiency));
            }
            break;
          }

          if (k === (data[i].plotPoints.length - 1)) {
            GraphEfficiencyDataRow.push(null);
          }

        }

      }

      tempGraphEfficiencyData.push(GraphEfficiencyDataRow);
    }
    const d36: any = 0.5 * air_density2 * Math.pow((req_airflow / netFreeArea), 2);
    const d37: any = d36 - (0.5 * air_density2 * (Math.pow((req_airflow) / (3.14 * (fanDetails.discharge_dia ?? 1) * (fanDetails.discharge_dia ?? 1) / 4), 2)) * (fanDetails.diffuser_eff ?? 1) / 100);
    let d35: any = null;
    let d38: any = null;
    if (fanDetails.pressure_type === "static") { //CHECK
      d38 = (parseFloat(fanDetails.pressure) + parseFloat(d36) - (isNaN(d37) ? 0 : d37));
      //d38 = (fanDetails.pressure + d36 - d37);
      // d35 = (parseFloat(fanDetails.pressure) - parseFloat(d36) + (isNaN(d37)?0:d37));
      d35 = fanDetails.pressure;

    } else {
      d38 = fanDetails.pressure;
      // d35 = (fanDetails.pressure - (isNaN(d37)?0:d37));
      d35 = (parseFloat(fanDetails.pressure) - parseFloat(d36) + (isNaN(d37) ? 0 : d37));
    }
    const d39 = req_airflow * d38 * 0.1 / (fanData.designPoint.expectedEfficiency);
    const d44 = req_airflow * d35 / (10 * d39);
    if (designPoint) {

      let tempGraphEfficiencyDataDesignPoint = tempGraphEfficiencyData;

      tempGraphEfficiencyDataDesignPoint[0].push("Design Point");

      for (let i = 1; i < tempGraphEfficiencyData.length; i++) {
        tempGraphEfficiencyDataDesignPoint[i].push(null);
      }

      let GraphEfficiencyDataRow = [];
      GraphEfficiencyDataRow.push(parseInt(fanDetails.req_airflow));

      for (let j = 2; j < graphHeaderData.length; j++) {
        GraphEfficiencyDataRow.push(null);
      }
      if (curveType)
        GraphEfficiencyDataRow.push(d44);
      else
        GraphEfficiencyDataRow.push(fanData.designPoint.expectedEfficiency);
      tempGraphEfficiencyData.push(GraphEfficiencyDataRow);

    }
    const res = appendInterpolatedPoints(tempGraphEfficiencyData)
    console.log(res);
    setGraphEfficiencyData(tempGraphEfficiencyData);
    console.log(tempGraphEfficiencyData);
    setEfficiencyReady(true);
  }

  if (props.onlyGraph) {
    return (
      <>
        {pressureReady && <Chart
          width={'100%'}
          height={'700px'}
          chartType="LineChart"
          loader={<div>Loading Chart</div>}
          data={
            graphPressureData
          }
          options={{
            ...options,
            interpolateNulls: false,
            seriesType: 'line',
            series: { [graphPressureData[0].length - 2]: { type: 'scatter' } },
            // hAxis: {
            //   title: 'Airflow (m³/sec)', // X axis unit
            // },
            // vAxis: {
            //   title: curveType ? 'Static Pressure(Pascal)' : 'Total Pressure(Pascal)', // <-- Add unit here
            // }
          }}
        />}

        <Chart
          width={'100%'}
          height={'700px'}
          chartType="LineChart"
          loader={<div>Loading Chart</div>}
          data={
            graphShaftData
          }
          options={{
            ...options2,
            series: { [graphShaftData[0]?.length - 2]: { type: 'scatter' } },
            // hAxis: {
            //   title: 'Airflow (m³/sec)', // X axis unit
            // },
            // vAxis: {
            //   title: 'Fan Shaft Power(kW)', // <-- Add unit here
            // }
          }}
        />

        {efficiencyReady && <Chart
          width={'100%'}
          height={'700px'}
          chartType="LineChart"
          loader={<div>Loading Chart</div>}
          data={
            graphEfficiencyData
          }
          options={{
            ...options3,
            seriesType: 'line',
            series: { [graphEfficiencyData[0].length - 2]: { type: 'scatter' } },
          }}
        />}
      </>
    )
  } else {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '1200px',
        padding: '20px'
      }}>

        <Typography variant="h5" gutterBottom>
          <b>Performance Curve</b>
        </Typography>
        <Table aria-label="simple table" style={{ zoom: 0.8 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" colSpan={1} rowSpan={2}>
                Airflow
              </TableCell>
              <TableCell align="center" colSpan={5}>
                Angles
              </TableCell>
            </TableRow>
            <TableRow>
              {angles.map((angle, i) => {
                return (
                  <TableCell key={"angle" + i} align="right">
                    <Typography variant="h5">{angle}°</Typography>
                    {/*<TextField variant="outlined" placeholder='angle°' defaultValue={angle}
                         onChange={e=>onPlotpointChageAngle(e.target.value,i,angle)}/>*/}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {graphPressureData.map((row, index) => (
              ((index !== 0 && (designPoint ? (index !== graphPressureData.length - 1) : true)) ?
                <TableRow
                  key={row[0]}
                >

                  <TableCell component="th" scope="row">
                    {row[0].toFixed(2)}
                    {/* {(fanDetails && fanDetails.fan_speed!=="")?(row[0]*((parseInt(fanDetails.fan_speed))/props.rpm)).toFixed(2):row[0]} */}
                    {/* <TextField variant="outlined" placeholder='Blank' type="number"  value={(fanDetails && fanDetails.fan_speed!=="")?(row[0]*((parseInt(fanDetails.fan_speed))/props.rpm)).toFixed(2):row[0].toFixed(2)}/> */}
                    {/* <TextField variant="outlined" placeholder='Airflow' defaultValue={row[0].toFixed(2)}
                  onChange={e=>onPlotpointChageAirflow(parseFloat(e.target.value),index-1)}
            />*/}

                  </TableCell>
                  {angles.map((angle, i) => {
                    return (
                      <TableCell align="right">
                        {/*<TextField variant="outlined" placeholder='Total Pressure' onChange={e=>onPlotpointChage(row[0],angle,parseFloat(e.target.value))} defaultValue={row[i+1]?row[i+1].toFixed(2):''} />*/}
                        {row[i + 1] ? row[i + 1].toFixed(2) : '0'}
                      </TableCell>
                    )
                  })}
                </TableRow>
                : null)
            ))}
          </TableBody>
        </Table>
        <br />
        <br />
        <div style={{ display: 'flex', width: '100%', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Typography variant="h5" style={{ opacity: curveType ? 0.5 : 1 }}>Total Pressure Curves</Typography>
            <Switch
              checked={curveType}
              onChange={(e) =>
                dispatch(setPressureCurveMode(e.target.checked ? "static" : "total"))}
              name="checkedCurve"
              inputProps={{ 'aria-label': 'secondary checkbox' }}
            />
            <Typography variant="h5" style={{ opacity: curveType ? 1 : 0.5 }}>Static Pressure Curves</Typography>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Switch
              checked={designPoint}
              onChange={(e) => setDesignPoint(e.target.checked)}
              name="checkedDP"
              inputProps={{ 'aria-label': 'secondary checkbox' }}
            />
            <Typography variant="h5">Show Design Point</Typography>
          </div>
        </div>
        <br />
        <br />
        <br />

        <div style={{ width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            <b>{curveType ? "Static Pressure vs Airflow" : "Total Pressure vs Airflow"}</b>
          </Typography><br />
          {pressureReady && <Chart
            width={'100%'}
            height={'400px'}
            chartType="LineChart"
            loader={<div>Loading Chart</div>}
            data={
              graphPressureData
            }
            options={designPoint ? {
              ...options,
              seriesType: 'line',
              series: { [graphPressureData[0].length - 2]: { type: 'scatter' }, },
            } : options}
          />}
        </div>

        <br />

        <div style={{ width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            <b>Fan Shaft Power vs Airflow</b>
          </Typography><br />
          {graphShaftData.length && <Chart
            width={'100%'}
            height={'400px'}
            chartType="LineChart"
            loader={<div>Loading Chart</div>}
            data={
              graphShaftData
            }
            options={designPoint ? {
              ...options2,
              seriesType: 'line',
              series: { [graphShaftData[0].length - 2]: { type: 'scatter' } },
            } : options2}
          />
          }

        </div>

        <br />

        <div style={{ width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            <b>{curveType ? "Static Efficiency vs Airflow" : "Total Efficiency vs Airflow"}</b>
          </Typography><br />
          {efficiencyReady && <Chart
            width={'100%'}
            height={'400px'}
            chartType="LineChart"
            loader={<div>Loading Chart</div>}
            data={
              graphEfficiencyData
            }
            options={designPoint ? {
              ...options3,
              seriesType: 'line',
              series: { [graphEfficiencyData[0].length - 2]: { type: 'scatter' } },
            } : options3}
          />}
        </div>

        <br />
        <br />
        <div style={{ display: 'flex', width: '100%', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Typography variant="h5" style={{ opacity: curveType ? 0.5 : 1 }}>Total Pressure Curves</Typography>
            <Switch
              checked={curveType}
              onChange={(e) =>
                dispatch(setPressureCurveMode(e.target.checked ? "static" : "total"))}
              name="checkedCurve"
              inputProps={{ 'aria-label': 'secondary checkbox' }}
            />
            <Typography variant="h5" style={{ opacity: curveType ? 1 : 0.5 }}>Static Pressure Curves</Typography>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Switch
              checked={designPoint}
              onChange={(e) => setDesignPoint(e.target.checked)}
              name="checkedDP"
              inputProps={{ 'aria-label': 'secondary checkbox' }}
            />
            <Typography variant="h5">Show Design Point</Typography>
          </div>
        </div>
        <br />

        <br />
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start'
          }}>
          <Button
            variant="outlined"
            // color="primary"
            size="large"
            onClick={() => props.setIndex(2)}
            style={{ backgroundColor: '#6b6666', color: 'white' }}
          >
            Back
          </Button>
          <span style={{ padding: '20px' }} />
          <Button
            // type="submit"
            variant="contained"
            color="primary"
            size="large"
            onClick={() => props.setIndex(4)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  }
}